// Plan upsell logic for the sidebar card. Pure so it can be unit-tested.

export interface PlanInfo {
  name: string;
  tier: string;
  displayName: string;
  monthlyPrice: number;
  monthlyTokenLimit: number;
  features: string[];
}

export interface Upsell {
  /** Plan being offered (the next tier up). */
  target: PlanInfo;
  title: string;
  body: string;
  cta: string;
  /** True when the user is close to their current limit. */
  urgent: boolean;
}

/** Only self-serve tiers below this are upsold; Professional and up are not. */
const UPSELL_FROM = new Set(["free", "starter"]);

const fmtPrice = (n: number) => (Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`);

/**
 * Pick the next plan up for the user's current tier, or null if no upsell
 * applies (admins, top tiers, custom plans, unknown/loading subscription).
 */
export function getUpsell(opts: {
  tier: string | null | undefined;
  isAdmin: boolean;
  plans: PlanInfo[];
  tokensUsed?: number;
  tokensLimit?: number;
}): Upsell | null {
  const tier = (opts.tier || "").toLowerCase();
  if (opts.isAdmin || !tier || !UPSELL_FROM.has(tier)) return null;

  const ladder = [...opts.plans].sort((a, b) => a.monthlyPrice - b.monthlyPrice);
  const idx = ladder.findIndex((p) => p.tier.toLowerCase() === tier || p.name.toLowerCase() === tier);
  if (idx < 0 || idx === ladder.length - 1) return null;
  const current = ladder[idx];
  const target = ladder[idx + 1];

  const usedPct = opts.tokensLimit ? Math.round(((opts.tokensUsed || 0) / opts.tokensLimit) * 100) : 0;
  const urgent = usedPct >= 80;

  const multiplier =
    current.monthlyTokenLimit > 0 ? Math.round(target.monthlyTokenLimit / current.monthlyTokenLimit) : 0;
  const tokenLine = multiplier >= 2 ? `${multiplier}× your monthly tokens` : "";

  // Features the next plan adds, skipping token/request-quota lines (covered above).
  const currentSet = new Set(current.features.map((f) => f.toLowerCase()));
  const extras = target.features
    .filter((f) => !currentSet.has(f.toLowerCase()) && !/tokens\/month|requests\/month/i.test(f))
    .slice(0, 2)
    .map((f) => f.charAt(0).toLowerCase() + f.slice(1));

  const parts = [tokenLine, ...extras].filter(Boolean);
  const offer = parts.length > 1 ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}` : parts[0] || "";

  return {
    target,
    urgent,
    title: urgent ? `You've used ${usedPct}% of this month's tokens` : `Upgrade to ${target.displayName}`,
    body: `${offer ? offer.charAt(0).toUpperCase() + offer.slice(1) : `More of everything on ${target.displayName}`} — ${fmtPrice(target.monthlyPrice)}/month.`,
    cta: `Upgrade to ${target.displayName}`,
  };
}
